import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import useAuth from 'src/hooks/useAuth'
import {
  RECENT_ANALYSES_KEY,
  RECENT_ANALYSES_OPTIONS,
  fetchRecentAnalyses,
} from 'src/views/NewAnalysis/useUploadedNotAnalysed'
import { userKeyOf } from 'src/views/NewAnalysis/model/storage'
import {
  buildSuggestions,
  dismiss as dismissSetup,
  dismissedKey,
  isDismissed,
  loadDismissed,
  mineSetups,
  saveDismissed,
  undismiss as undismissSetup,
} from './suggestions'

// ----------------------------------------------------------------------
// The data of Home's "Suggested" card: setups mined from the recent analyses (the
// list the batches and In progress already read, [BATCHES_QUERY_KEY], no timer of
// its own) paired with the samples of "Ready to analyse", minus the setups hidden
// with "Not now" (per user, in this browser, for DISMISS_DAYS days).
//
// ready: what useReadySamples returns ({ items, loaded, failed }), passed down from
// Home so both cards agree on which samples are hidden.
// ----------------------------------------------------------------------

const useSuggestions = (ready) => {
  const { user } = useAuth() || {}
  const userKey = userKeyOf(user)
  const query = useQuery(RECENT_ANALYSES_KEY, fetchRecentAnalyses, RECENT_ANALYSES_OPTIONS)
  const rows = query.data?.results

  const setups = useMemo(() => (Array.isArray(rows) ? mineSetups(rows, Date.now()) : []), [rows])

  // ------------------------------------------------------------ "Not now"
  const [dismissed, setDismissed] = useState(() => loadDismissed(userKey))
  const dismissedRef = useRef(dismissed)
  dismissedRef.current = dismissed
  const dismissedFor = useRef(userKey)
  useEffect(() => {
    if (dismissedFor.current === userKey) return
    dismissedFor.current = userKey
    setDismissed(loadDismissed(userKey))
  }, [userKey])

  // Another tab dismissing a suggestion updates this one.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === dismissedKey(userKey)) setDismissed(loadDismissed(userKey))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [userKey])

  const change = useCallback(
    (update) => {
      const next = update(dismissedRef.current)
      dismissedRef.current = next
      saveDismissed(userKey, next)
      setDismissed(next)
    },
    [userKey],
  )
  const dismiss = useCallback((signature) => change((prev) => dismissSetup(prev, signature)), [change])
  const undismiss = useCallback((signature) => change((prev) => undismissSetup(prev, signature)), [change])

  // ------------------------------------------------------------ suggestions
  const readyItems = ready?.items
  const readyLoaded = Boolean(ready?.loaded)
  // Without the uploads (still loading or failed), setups show without samples only
  // once it is clear the uploads will not come.
  const readySettled = readyLoaded || Boolean(ready?.failed)
  const { suggestions, anyDismissed } = useMemo(() => {
    const now = Date.now()
    const open = setups.filter((setup) => !isDismissed(dismissed, setup.signature, now))
    return {
      suggestions: buildSuggestions(open, readyLoaded ? readyItems : [], now),
      anyDismissed: open.length < setups.length,
    }
  }, [setups, dismissed, readyItems, readyLoaded])

  return {
    suggestions,
    loaded: Boolean(query.data) && readySettled,
    failed: !query.data && query.isError,
    retry: query.refetch,
    // Nothing to suggest because the user said "Not now" to it.
    allDismissed: suggestions.length === 0 && anyDismissed,
    dismiss,
    undismiss,
  }
}

export default useSuggestions
